import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { motion } from "framer-motion";
import {
  BadgeCheck,
  Briefcase,
  CheckCircle2,
  Clock,
  ExternalLink,
  FileText,
  GraduationCap,
  Mail,
  UserX,
  XCircle,
} from "lucide-react";
import PageHeader from "@/components/shared/PageHeader";
import StatusChip from "@/components/shared/StatusChip";
import SectionCard from "@/components/shared/SectionCard";
import { FullPageState } from "@/components/shared/FullPageState";
import { useAdminStudents, useApproveStudent } from "@/hooks/useAdminData";
import { PageSkeleton } from "@/components/shared/Skeletons";
import { getFileUrl } from "@/lib/api-client";
import apiClient from "@/lib/api-client";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Field } from "@/components/forms";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/admin/students/$id")({
  component: AdminStudentDetailPage,
});

const VERIFICATION_VARIANT: Record<string, "success" | "warning" | "destructive" | "muted"> = {
  verified: "success",
  pending_review: "warning",
  rejected: "destructive",
};

function AdminStudentDetailPage() {
  const { id } = Route.useParams();
  const { data: students = [], isLoading } = useAdminStudents();
  const s: any = students.find((x: any) => String(x.id) === id);
  const approveMutation = useApproveStudent();
  const queryClient = useQueryClient();

  const [approved, setApproved] = useState(false);
  const [rejectDialog, setRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [verifying, setVerifying] = useState(false);

  if (isLoading) {
    return <PageSkeleton />;
  }

  if (!s) {
    return (
      <FullPageState
        icon={<UserX />}
        title="Student not found"
        description="This student record does not exist or has been removed."
      />
    );
  }

  const status = approved ? "active" : s.status;
  const verificationStatus = s.verification_status || "unverified";

  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return "N/A";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "N/A";
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
  };

  const handleVerify = async (newStatus: string) => {
    setVerifying(true);
    try {
      await apiClient.post(`/admin/students/${s.user_id}/verify`, {
        status: newStatus,
        reason: newStatus === "rejected" ? rejectReason : "",
      });
      toast.success(
        newStatus === "verified"
          ? "Student verified successfully"
          : "Student verification rejected",
      );
      queryClient.invalidateQueries({ queryKey: ["admin-students"] });
      if (newStatus === "verified") setApproved(true);
      setRejectDialog(false);
    } catch (err: any) {
      toast.error("Failed to update verification", {
        description: err.response?.data?.error || err.message,
      });
    } finally {
      setVerifying(false);
    }
  };

  const facts = [
    { icon: <Mail />, label: "Email", value: s.email },
    { icon: <GraduationCap />, label: "Year of study", value: s.year ? `Year ${s.year}` : "Not specified" },
    { icon: <Clock />, label: "Hours logged", value: `${s.hoursLogged || 0} hours` },
    {
      icon: <Briefcase />,
      label: "Current placement",
      value: s.currentPlacement || "No active placement",
    },
  ];

  const documents = [
    { label: "Student ID", url: s.student_id_url },
    { label: "Dean's Letter", url: s.dean_letter_url },
    { label: "Resume", url: s.resume_url },
  ];

  const info: { label: string; value: string }[] = [
    { label: "Registered", value: formatDate(s.created_at) },
    { label: "University", value: s.university || "Not specified" },
    { label: "Major / course", value: s.course || "Not specified" },
    { label: "Phone", value: s.phone || "Not provided" },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title={s.name}
        breadcrumbs={[{ label: "Students", path: "/admin/students" }, { label: s.name }]}
      />

      {approved && (
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}>
          <Alert variant="success">
            <CheckCircle2 className="size-4" />
            <AlertDescription>Student account has been verified successfully.</AlertDescription>
          </Alert>
        </motion.div>
      )}

      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(280px,1fr)]">
        <div className="flex min-w-0 flex-col gap-4">
          <section className="rounded-md border border-border bg-card p-4 md:p-5">
            <div className="flex items-center gap-4">
              <Avatar className="size-14 rounded-full">
                {s.avatar_url ? <AvatarImage src={getFileUrl(s.avatar_url)} alt="" /> : null}
                <AvatarFallback className="rounded-full text-lg">
                  {s.name?.charAt(0) || "?"}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="truncate text-title text-foreground">{s.name}</h2>
                  <StatusChip status={status} />
                </div>
                <p className="mt-0.5 truncate text-sm text-muted-foreground">
                  {s.university || "No university"} {s.course ? `· ${s.course}` : ""}
                </p>
              </div>
            </div>

            <dl className="mt-5 grid grid-cols-1 gap-px overflow-hidden rounded-sm border border-border bg-border sm:grid-cols-2">
              {facts.map((f) => (
                <div key={f.label} className="flex items-start gap-3 bg-card p-3">
                  <span className="mt-0.5 text-muted-foreground [&_svg]:size-4" aria-hidden>
                    {f.icon}
                  </span>
                  <div className="min-w-0">
                    <dt className="eyebrow">{f.label}</dt>
                    <dd className="mt-0.5 text-sm break-words text-foreground">{f.value}</dd>
                  </div>
                </div>
              ))}
            </dl>
          </section>

          <SectionCard title="Skills">
            {(s.skills || []).length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {(s.skills || []).map((skill: string) => (
                  <Badge key={skill} variant="outline">
                    {skill}
                  </Badge>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No skills listed</p>
            )}
          </SectionCard>

          <SectionCard title="Verification documents" noPadding>
            <ul className="divide-y divide-border">
              {documents.map((doc) => (
                <li
                  key={doc.label}
                  className="flex min-h-11 items-center gap-3 px-4 py-2.5 md:px-5"
                >
                  <FileText
                    className={cn(
                      "size-4 shrink-0",
                      doc.url ? "text-success" : "text-muted-foreground",
                    )}
                    aria-hidden
                  />
                  <span className="flex-1 text-sm text-foreground">{doc.label}</span>
                  {doc.url ? (
                    <Button variant="outline" size="sm" asChild>
                      <a href={getFileUrl(doc.url)} target="_blank" rel="noreferrer">
                        Preview
                        <ExternalLink />
                      </a>
                    </Button>
                  ) : (
                    <Badge variant="muted">Not uploaded</Badge>
                  )}
                </li>
              ))}
            </ul>
          </SectionCard>

          <SectionCard title="Verification status">
            <div className="flex flex-wrap items-center gap-3">
              <Badge variant={VERIFICATION_VARIANT[verificationStatus] ?? "muted"} dot={verificationStatus !== "verified"}>
                {verificationStatus === "verified" && <BadgeCheck className="size-3" aria-hidden />}
                {verificationStatus.replace("_", " ").toUpperCase()}
              </Badge>
              <span className="text-sm text-muted-foreground">
                Membership: <span className="text-foreground">{s.membership_tier || "free"}</span>
              </span>
            </div>

            {(verificationStatus === "pending_review" || verificationStatus === "unverified") &&
              !approved && (
                <div className="mt-4 flex flex-col gap-2 border-t border-border pt-4 sm:flex-row">
                  <Button
                    onClick={() => handleVerify("verified")}
                    disabled={verifying}
                    loading={verifying}
                  >
                    {!verifying && <CheckCircle2 />}
                    Verify student
                  </Button>
                  <Button variant="destructive-outline" onClick={() => setRejectDialog(true)}>
                    <XCircle />
                    Reject
                  </Button>
                </div>
              )}
          </SectionCard>
        </div>

        <SectionCard title="Student info" className="h-auto">
          <dl className="divide-y divide-border">
            {info.map((row) => (
              <div key={row.label} className="py-2.5 first:pt-0">
                <dt className="eyebrow">{row.label}</dt>
                <dd className="mt-0.5 text-sm text-foreground">{row.value}</dd>
              </div>
            ))}
            {s.bio && (
              <div className="py-2.5 last:pb-0">
                <dt className="eyebrow">Bio</dt>
                <dd className="mt-0.5 text-sm leading-6 text-body">{s.bio}</dd>
              </div>
            )}
          </dl>
        </SectionCard>
      </div>

      <Dialog open={rejectDialog} onOpenChange={(o) => !o && setRejectDialog(false)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Reject student verification</DialogTitle>
            <DialogDescription>
              The student will see this reason and can resubmit their documents.
            </DialogDescription>
          </DialogHeader>
          <Field label="Reason for rejection">
            <Textarea
              rows={4}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
            />
          </Field>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRejectDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => handleVerify("rejected")}
              disabled={verifying}
            >
              {verifying ? "Processing..." : "Confirm rejection"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
